export const FALLBACK_CURRENCY = "GHS";

const numberOr = (value, fallback = null) => {
  if (value === null || value === undefined || value === "") return fallback;

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : fallback;
};

export const amountValue = (money) => numberOr(money?.value);

export const amountCurrency = (money, fallback) =>
  money?.currency || fallback || FALLBACK_CURRENCY;

export const formatAmount = (value, currency) => {
  if (!Number.isFinite(value)) return "";

  const decimals = Number.isInteger(value) ? 0 : 2;

  const text = value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return `${currency || FALLBACK_CURRENCY} ${text}`;
};

export const formatMoney = (money, currency) =>
  formatAmount(amountValue(money), amountCurrency(money, currency));

const materialById = (dto, materialId) => {
  if (materialId === null || materialId === undefined) return null;

  return (
    (dto?.containerMaterials || []).find(
      (material) => String(material?.id) === String(materialId)
    ) || null
  );
};

const selfPriced = (material) => amountValue(material?.amount) !== null;

export const materialPrice = (dto, materialId) => {
  const material = materialById(dto, materialId);

  if (selfPriced(material)) {
    return {
      value: amountValue(material.amount),
      currency: amountCurrency(material.amount, amountCurrency(dto?.amount)),
    };
  }

  return {
    value: amountValue(dto?.amount),
    currency: amountCurrency(dto?.amount),
  };
};

export const materialOriginalPrice = (dto, materialId) => {
  const material = materialById(dto, materialId);

  if (selfPriced(material)) {
    const own = amountValue(material.originalAmount);

    if (own === null) return null;

    return {
      value: own,
      currency: amountCurrency(material.originalAmount, amountCurrency(dto?.amount)),
    };
  }

  const base = amountValue(dto?.originalAmount);

  if (base === null) return null;

  return {
    value: base,
    currency: amountCurrency(dto?.originalAmount, amountCurrency(dto?.amount)),
  };
};

export const isDiscount = (price, originalPrice) =>
  Number.isFinite(originalPrice?.value) &&
  Number.isFinite(price?.value) &&
  originalPrice.value > price.value;

export const materialInStock = (dto, materialId) => {
  if (dto?.inStock === false) return false;

  const material = materialById(dto, materialId);

  return material?.inStock !== false;
};

export const productPriceRange = (dto) => {
  const currency = amountCurrency(dto?.amount);

  const serverFrom = amountValue(dto?.minAmount);
  const serverTo = amountValue(dto?.maxAmount);

  if (serverFrom !== null && serverTo !== null) {
    return {
      from: serverFrom,
      to: serverTo,
      isRange: serverTo > serverFrom,
      currency: amountCurrency(dto?.minAmount, currency),
    };
  }

  const materials = dto?.containerMaterials || [];
  const priced = materials
    .map((material) => amountValue(material?.amount))
    .filter((value) => value !== null);

  const base = amountValue(dto?.amount);
  const anyUnpriced = !materials.length || priced.length < materials.length;

  const values = [...priced, ...(anyUnpriced && base !== null ? [base] : [])];

  if (!values.length) return { from: null, to: null, isRange: false, currency };

  const from = Math.min(...values);
  const to = Math.max(...values);

  return { from, to, isRange: to > from, currency };
};

export const materialPricesDiffer = (dto) => {
  const materials = dto?.containerMaterials || [];

  if (materials.length < 2) return false;

  const prices = materials.map((material) => materialPrice(dto, material?.id).value);

  return new Set(prices).size > 1;
};
